import React from "react";
import { Sparkle, TrendUp, TrendDown, Minus, Target } from "@phosphor-icons/react";

const recColor = (rec) => {
  if (!rec) return "#A1A1AA";
  const r = rec.toLowerCase();
  if (r.includes("strong_buy") || r === "buy") return "#34C759";
  if (r.includes("strong_sell") || r === "sell") return "#FF3B30";
  if (r === "hold" || r === "underperform" || r === "outperform") return "#FFCC00";
  return "#A1A1AA";
};

const recLabel = (rec) => {
  if (!rec) return "—";
  return rec.replace(/_/g, " ").toUpperCase();
};

const fmtMoney = (val, currency) => {
  if (val == null) return "—";
  const sym = currency === "INR" ? "₹" : currency === "EUR" ? "€" : currency === "GBP" ? "£" : "$";
  return `${sym}${Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

export default function ForecastCard({ data, loading }) {
  const t = data?.targets || {};
  const price = data?.price;
  const upside = data?.upside_pct;
  const currency = data?.currency;
  const upsideColor = upside == null ? "#A1A1AA" : upside >= 0 ? "#34C759" : "#FF3B30";
  const UpsideIcon = upside == null ? Minus : upside >= 0 ? TrendUp : TrendDown;
  const has = data?.has_targets;

  return (
    <div className="bg-surface border border-white/10 p-5" data-testid="forecast-card">
      <div className="flex items-center gap-2 mb-3">
        <Target size={18} weight="duotone" color="#FFCC00" />
        <h3 className="heading text-lg font-semibold">Forecast & Targets</h3>
        <span className="overline ml-auto">claude · sonnet 4.5</span>
      </div>

      {loading ? (
        <div className="space-y-2">
          <div className="h-3 bg-white/5 w-full" />
          <div className="h-3 bg-white/5 w-10/12" />
          <div className="h-3 bg-white/5 w-9/12" />
        </div>
      ) : (
        <>
          {has ? (
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">avg target</div>
                <div className="mono text-base text-white">{fmtMoney(t.target_mean, currency)}</div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">implied upside</div>
                <div className="mono text-base flex items-center gap-1" style={{ color: upsideColor }}>
                  <UpsideIcon size={14} weight="bold" />
                  {upside == null ? "—" : `${upside >= 0 ? "+" : ""}${upside.toFixed(2)}%`}
                </div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">range</div>
                <div className="mono text-xs text-white/80" data-testid="forecast-range">
                  {fmtMoney(t.target_low, currency)} – {fmtMoney(t.target_high, currency)}
                </div>
              </div>
              <div className="border border-white/5 px-3 py-2">
                <div className="overline">consensus</div>
                <div className="mono text-xs" style={{ color: recColor(t.recommendation) }}>
                  {recLabel(t.recommendation)}
                </div>
                <div className="mono text-[10px] text-white/40 mt-0.5">
                  {t.analyst_count ? `${t.analyst_count} analysts` : "—"}
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-white/10 px-3 py-2 mb-4 overline text-white/50">
              no street consensus available for this ticker · using technicals only
            </div>
          )}

          <div className="border-t border-white/10 pt-3">
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={14} weight="duotone" color="#FFCC00" />
              <span className="overline">ai outlook · 1-4 weeks</span>
            </div>
            <p className="text-sm leading-relaxed text-white/85" data-testid="forecast-narrative">
              {data?.narrative || "Awaiting forecast..."}
            </p>
          </div>
        </>
      )}
    </div>
  );
}
